/**
 * P0 DEFINITIVE FIX: Centralized POST-only Routes Registry
 * =========================================================
 * 
 * SINGLE SOURCE OF TRUTH for all routes that ONLY accept POST method.
 * Any GET/HEAD/PUT/PATCH/DELETE on these routes is a client-side bug.
 * 
 * This file is imported by:
 * - api.js (request interceptor)
 * - tests (validation)
 * 
 * @module postOnlyRoutes
 */

// =============================================================================
// POST-ONLY ROUTES REGISTRY (Global - All endpoints)
// =============================================================================

/**
 * Routes that ONLY accept POST method.
 * GET/HEAD/PUT/PATCH/DELETE will result in 405 Method Not Allowed.
 * 
 * Format: endpoint path WITHOUT /api prefix (since apiClient has baseURL=/api)
 */
export const POST_ONLY_ROUTES = [
  // =========================================================================
  // AUTH ROUTES (P0 FIX: Protect against cached GET requests)
  // =========================================================================
  '/auth/login',
  '/auth/signup',
  '/auth/register',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/request-password-reset',
  
  // =========================================================================
  // INSTAGRAM
  // =========================================================================
  '/social/instagram/test',
  '/social/instagram/publish',
  '/social/instagram/select-account',
  '/social/instagram/disconnect-hard',
  '/social/instagram/config',
  '/social/instagram/refresh-accounts',
  '/social/instagram/verify-token',
  
  // =========================================================================
  // FACEBOOK
  // =========================================================================
  '/social/facebook/test',
  '/social/facebook/select-page',
  // P1 FIX: REMOVED /social/facebook/config - this route supports BOTH GET and POST
  // The GET is used by MetaAppConfig.jsx to read existing configuration
  // '/social/facebook/config',
  
  // =========================================================================
  // SCHEDULER
  // =========================================================================
  '/scheduler/run',
  
  // =========================================================================
  // DEBUG/SEED (POST-only for data mutation)
  // =========================================================================
  '/debug/seed-instagram-content',
  
  // =========================================================================
  // AUTH ACTIONS (POST-only for security)
  // =========================================================================
  '/auth/x/disconnect',
  '/auth/x/test-post',
  
  // =========================================================================
  // INTEGRATIONS (POST-only for credential mutation)
  // =========================================================================
  '/integrations/x/credentials',
  '/integrations/telegram',
  '/integrations/telegram/test',
  '/integrations/telegram/fetch-chat-id',
  '/integrations/telegram/sample-post',
  '/integrations/facebook',
  
  // =========================================================================
  // AI GENERATION (P0 FIX: Added)
  // =========================================================================
  '/ai/generate-multiplatform',
  '/ai/generate-content',
  '/ai/generate-image',
  '/ai/generate-video',
  
  // =========================================================================
  // PINTEREST (P0 FIX: Added to prevent 405 errors)
  // =========================================================================
  '/social/pinterest/test',
  '/social/pinterest/select-board',
  '/social/pinterest/config',
  '/social/pinterest/disconnect',
  
  // =========================================================================
  // CONTENT PUBLISH (P0 FIX: Dynamic route - matches /contents/*/publish)
  // =========================================================================
  '/contents/',  // Will match /contents/{id}/publish via startsWith
];

/**
 * Routes that explicitly allow GET/HEAD for health checks or diagnostics.
 * These routes bypass the POST-only guard.
 */
export const GET_SAFE_ROUTES = [
  '/health',
  '/version',
  '/env-check',
  '/db-test',
  '/debug/env',
  '/social/instagram/diag',
  '/social/instagram/status',
  '/social/instagram/accounts',
  '/social/facebook/diag',
  '/social/facebook/pages',
  '/social/facebook/callback-status',
  // P1 FIX: Facebook config and OAuth connect must be GET-accessible
  '/social/facebook/config',
  '/auth/facebook/connect',
  '/integrations/callback-urls',
  '/integrations/x/status',
  '/integrations/telegram/status',
  '/integrations/facebook/status',
  '/audit/instagram',
  '/debug/instagram-credentials',
  '/debug/instagram-publish-readiness',
  // OAuth callbacks (GET from external providers)
  '/auth/x/callback',
  '/auth/x/start',
  '/auth/x/diagnostic',
];

/**
 * Methods that are NEVER allowed on POST-only routes.
 * OPTIONS is excluded (used for CORS preflight).
 */
export const FORBIDDEN_METHODS_ON_POST_ONLY = ['GET', 'HEAD', 'PUT', 'PATCH', 'DELETE'];

/**
 * Check if a route is POST-only.
 * 
 * IMPORTANT: GET_SAFE_ROUTES has priority. If a route is explicitly 
 * listed as GET-safe, it is NOT considered POST-only even if a parent
 * route matches (e.g., /telegram/status is GET-safe even though /telegram is POST-only).
 * 
 * @param {string} url - The URL path (with or without /api prefix)
 * @returns {boolean} True if route is POST-only (and NOT GET-safe)
 */
export function isPostOnlyRoute(url) {
  if (!url) return false;
  
  // Normalize: remove leading /api if present
  const normalizedUrl = url.toLowerCase().replace(/^\/api/, '');
  
  // P0 FIX: GET-safe routes have PRIORITY over POST-only matching
  // Check if this exact route is GET-safe first
  const isExplicitlyGetSafe = GET_SAFE_ROUTES.some(route =>
    normalizedUrl === route.toLowerCase() ||
    normalizedUrl.startsWith(route.toLowerCase() + '?')
  );
  
  if (isExplicitlyGetSafe) {
    return false; // GET-safe routes are NOT POST-only
  }
  
  // P0 FIX: Special handling for /contents/:id/publish (dynamic route)
  // This route MUST be POST-only for publishing content
  const contentsPublishPattern = /^\/contents\/[^/]+\/publish/i;
  if (contentsPublishPattern.test(normalizedUrl)) {
    return true;
  }
  
  // Now check POST-only routes
  return POST_ONLY_ROUTES.some(route => 
    normalizedUrl === route.toLowerCase() ||
    normalizedUrl.startsWith(route.toLowerCase() + '?') ||
    normalizedUrl.startsWith(route.toLowerCase() + '/')
  );
}

/**
 * Check if a route is explicitly GET-safe.
 * 
 * @param {string} url - The URL path
 * @returns {boolean} True if route allows GET/HEAD
 */
export function isGetSafeRoute(url) {
  if (!url) return false;
  
  const normalizedUrl = url.toLowerCase().replace(/^\/api/, '');
  
  return GET_SAFE_ROUTES.some(route =>
    normalizedUrl === route.toLowerCase() ||
    normalizedUrl.startsWith(route.toLowerCase() + '?') ||
    normalizedUrl.startsWith(route.toLowerCase() + '/')
  );
}

/**
 * P0 DEFINITIVE: Detect invalid method on any route (not just Instagram).
 * 
 * Returns detailed violation info for logging and error throwing.
 * 
 * IMPORTANT: GET_SAFE_ROUTES has priority over POST_ONLY_ROUTES matching.
 * This prevents false positives when a sub-route (e.g., /telegram/status) 
 * is GET-safe but parent route (/telegram) is POST-only.
 * 
 * @param {string} url - The URL being called
 * @param {string} method - The HTTP method being used
 * @returns {Object|null} Violation details or null if valid
 */
export function detectInvalidMethodOnRoute(url, method) {
  if (!url || !method) return null;
  
  const normalizedMethod = method.toUpperCase();
  
  // OPTIONS is always allowed (CORS preflight)
  if (normalizedMethod === 'OPTIONS') return null;
  
  // P0 FIX: Check GET-safe routes FIRST (they have priority)
  // This prevents /integrations/telegram/status from being blocked
  // when /integrations/telegram is POST-only
  if (isGetSafeRoute(url)) {
    // GET-safe routes allow GET/HEAD
    if (normalizedMethod === 'GET' || normalizedMethod === 'HEAD') {
      return null;
    }
  }
  
  // Check if route is POST-only
  if (!isPostOnlyRoute(url)) return null;
  
  // POST is always allowed on POST-only routes
  if (normalizedMethod === 'POST') return null;
  
  // Any other method on POST-only route is a violation
  if (FORBIDDEN_METHODS_ON_POST_ONLY.includes(normalizedMethod)) {
    // Find which POST-only route was matched
    const normalizedUrl = url.toLowerCase().replace(/^\/api/, '');
    
    // P0 FIX: Check if it's a /contents/:id/publish dynamic route
    const contentsPublishPattern = /^\/contents\/[^/]+\/publish/i;
    let matchedRoute = null;
    
    if (contentsPublishPattern.test(normalizedUrl)) {
      matchedRoute = '/contents/:id/publish';
    } else {
      matchedRoute = POST_ONLY_ROUTES.find(route =>
        normalizedUrl === route.toLowerCase() ||
        normalizedUrl.startsWith(route.toLowerCase() + '?') ||
        normalizedUrl.startsWith(route.toLowerCase() + '/')
      );
    }
    
    return {
      isViolation: true,
      url,
      method: normalizedMethod,
      expectedMethod: 'POST',
      matchedRoute: matchedRoute || 'unknown',
      message: `Invalid method ${normalizedMethod} on POST-only route. Use POST instead.`,
      fix: 'Replace api.get() with api.post() or projectGet() with projectPost()',
      timestamp: new Date().toISOString(),
    };
  }
  
  return null;
}

/**
 * P0 HARDENING: Create a descriptive error for method violation.
 * 
 * @param {Object} violation - Violation details from detectInvalidMethodOnRoute
 * @returns {Error} Error object with detailed message and metadata
 */
export function createMethodViolationError(violation) {
  const error = new Error(
    `[405_PREVENTION] ${violation.method} not allowed on ${violation.matchedRoute}. ` +
    `This route only accepts POST. ${violation.fix}`
  );
  error.name = 'MethodNotAllowedError';
  error.code = 'ERR_METHOD_NOT_ALLOWED';
  error.violation = violation;
  return error;
}

// =============================================================================
// EXPORTS
// =============================================================================

export default {
  POST_ONLY_ROUTES,
  GET_SAFE_ROUTES,
  FORBIDDEN_METHODS_ON_POST_ONLY,
  isPostOnlyRoute,
  isGetSafeRoute,
  detectInvalidMethodOnRoute,
  createMethodViolationError,
};
